import { pi } from 'mathjs'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoSphericalPlate,
  GeckoSphericalPlateJSON,
} from 'types/assetModel/features/containmentDome/geckoSphericalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export type DomeAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.Dome
  radius: number
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  features: Record<string, GeckoSphericalPlateJSON>
  compass_orientation?: number
}

export const DomeAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.SphericalPlate,
]

export const NewDomeAssetModelJSON: DomeAssetModelJSON = {
  name: 'New Dome',
  type: GeckoComponentModelType.Dome,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
}

export class GeckoDome extends GeckoEditableComponent {
  type: GeckoComponentModelType.Dome
  radius: GeckoMeasurement
  sceneConfig = undefined
  componentBounds: GeckoComponentRect
  plates: Map<string, GeckoSphericalPlate>

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    plates: Map<string, GeckoSphericalPlate>
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.Dome
    this.radius = radius
    this.componentBounds = componentBounds
    this.plates = plates
  }

  public static CreateFromJSON(id: string, json: DomeAssetModelJSON) {
    const name = json.name
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )

    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      0,
      0,
      360,
      90,
      GeckoMeasurementUnit.Degrees,
      GeckoMeasurementUnit.Degrees
    )
    const plates = GeckoSphericalPlate.CreateMapFromJSON(
      id,
      json.features as Record<string, GeckoSphericalPlateJSON>
    )
    return new GeckoDome(id, json, name, radius, componentBounds, plates)
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public asJSON(): DomeAssetModelJSON {
    return generateDome(this.name, this.radius, this.json as DomeAssetModelJSON)
  }
}

const generateDome = (
  name: string,
  radius: GeckoMeasurement,
  otherData: DomeAssetModelJSON = NewDomeAssetModelJSON
): DomeAssetModelJSON => {
  const tubeRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tubeCircumference = Number(
    (2 * pi * tubeRadius).toPrecision(assetModelPrecision)
  )

  return {
    name: name,
    type: GeckoComponentModelType.Dome,
    radius: tubeRadius,
    scene_config: otherData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tubeCircumference,
      y_end: tubeCircumference,
    },
    features: otherData.features,
  }
}

export const DomeAssetModelFormFields = (JSON: DomeAssetModelJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Containment Dome Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },

    {
      dataKey: 'radius',
      label: 'Diameter',
      value: JSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const DomeJSONFromFormFields = (
  component: DomeAssetModelJSON,
  formFields: FormField[]
) => {
  return generateDome(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    component
  )
}
